import { useCallback, useMemo, useState } from "react";
import type { CameraRole, Role } from "../../schemas";
import type { ListRoleSelectorItem } from "../components/ListRoleSelector";
import { useAccessManagement } from "./useAccessManagement";
import { useAddCameraPermission } from "./useAddCameraPermission";

type UiRole = "manager" | "viewer";

const toUiRole = (role: CameraRole): UiRole => (role === "camera_manager" ? "manager" : "viewer");
const toCameraRole = (role: UiRole): CameraRole => (role === "manager" ? "camera_manager" : "camera_viewer");

/**
 * Parse a site-scoped role string (e.g. `site_manager:S0-abc`) into its siteId and UI role.
 * Returns null for non-site roles (e.g. `tenant_admin`).
 */
export function parseSiteRole(role: Role): { siteId: string; role: UiRole } | null {
  if (role.startsWith("site_manager:")) return { siteId: role.substring("site_manager:".length), role: "manager" };
  if (role.startsWith("site_user:")) return { siteId: role.substring("site_user:".length), role: "viewer" };
  return null;
}

export type ManageCameraAccessCallbacks = {
  onRoleChangeSuccess?: (userName: string, newRole: UiRole) => void;
  onRoleChangeError?: () => void;
};

export type ManageCameraAccessItem = ListRoleSelectorItem & { email: string; phoneNumber: string };

export function useManageCameraAccess({
  siteId,
  cameraKey,
  onRoleChangeSuccess,
  onRoleChangeError,
}: { siteId?: string; cameraKey?: string } & ManageCameraAccessCallbacks) {
  const cameraId = siteId && cameraKey ? `${siteId}/${cameraKey}` : null;

  const { data, isLoading } = useAccessManagement();
  const addCameraPermission = useAddCameraPermission();

  const cameraUsers = useMemo<ManageCameraAccessItem[]>(() => {
    if (!data || !cameraId) return [];
    const byId = new Map(data.users.map((u) => [String(u.id), u]));
    return data.cameras.flatMap((assignment) => {
      if (assignment.camera !== cameraId) return [];
      const u = byId.get(String(assignment.user));
      if (!u) return [];
      return [
        {
          id: String(u.id),
          name: [u.givenName, u.familyName].filter(Boolean).join(" ") || u.email,
          email: u.email,
          phoneNumber: u.phoneNumber ?? "",
          role: toUiRole(assignment.role),
        },
      ];
    });
  }, [data, cameraId]);

  const tenantAdmins = useMemo<ManageCameraAccessItem[]>(() => {
    if (!data) return [];
    const adminIds = new Set(data.roles.filter((r) => r.role === "tenant_admin").map((r) => String(r.user)));
    return data.users
      .filter((u) => adminIds.has(String(u.id)))
      .map((u) => ({
        id: String(u.id),
        name: [u.givenName, u.familyName].filter(Boolean).join(" ") || u.email,
        email: u.email,
        phoneNumber: u.phoneNumber ?? "",
        role: "manager" as const,
      }));
  }, [data]);

  const siteWideUsers = useMemo<ManageCameraAccessItem[]>(() => {
    if (!data || !siteId) return [];
    const byId = new Map(data.users.map((u) => [String(u.id), u]));
    return data.roles.flatMap((r) => {
      const parsed = parseSiteRole(r.role);
      if (!parsed || parsed.siteId !== siteId) return [];
      const u = byId.get(String(r.user));
      if (!u) return [];
      return [
        {
          // Composite id so the same user can appear once as manager and once as viewer without key collisions.
          id: `${String(u.id)}:${parsed.role}`,
          name: [u.givenName, u.familyName].filter(Boolean).join(" ") || u.email,
          email: u.email,
          phoneNumber: u.phoneNumber ?? "",
          role: parsed.role,
        },
      ];
    });
  }, [data, siteId]);

  const [errorItemIds, setErrorItemIds] = useState<Set<string>>(new Set());

  const handleCameraUserRoleChange = useCallback(
    (userId: string, role: UiRole) => {
      if (!cameraId) return;
      const target = cameraUsers.find((u) => u.id === userId);
      setErrorItemIds((prev) => {
        if (!prev.has(userId)) return prev;
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
      addCameraPermission.mutate(
        { user: userId, camera: cameraId, role: toCameraRole(role) },
        {
          onSuccess: () => {
            if (target) onRoleChangeSuccess?.(target.name, role);
          },
          onError: () => {
            setErrorItemIds((prev) => new Set(prev).add(userId));
            onRoleChangeError?.();
          },
        },
      );
    },
    [cameraId, cameraUsers, addCameraPermission, onRoleChangeSuccess, onRoleChangeError],
  );

  return {
    isLoading,
    cameraUsers,
    tenantAdmins,
    siteWideUsers,
    errorItemIds,
    handleCameraUserRoleChange,
  };
}
